import React from 'react';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';

/** Popup toast titles only (Active Alerts panel uses icons, not these). */
export const POPUP_EMOJI = {
  ok: '✓',
  error: '❌',
  warning: '⚠️',
};

/** Remove legacy text/emoji prefixes from stored alert titles. */
export function stripAlertTitlePrefix(title) {
  if (!title || typeof title !== 'string') return title;
  return title
    .replace(/^Target\s+(UP|DOWN|TIMEOUT):\s*/i, '$1: ')
    .replace(/^\[(OK|DOWN|TIMEOUT|!|ERR|CRIT|WARN|CRITICAL)\]\s*/i, '')
    .replace(/^(\u2713|\u2717|\u274c|\u26a0\ufe0f|\ud83d\udd34)\s*/u, '')
    .trim();
}

/** Active Alerts list — plain title (icon rendered separately). */
export function formatTargetAlertTitle(status, targetName, isRecovery = false) {
  if (isRecovery) return `Recovered: ${targetName}`;
  return `${status}: ${targetName}`;
}

export function formatInfluxAlertTitle(targetName, isAnomaly = true) {
  const kind = isAnomaly ? 'InfluxDB Anomaly' : 'InfluxDB Error';
  return `${kind}: ${targetName}`;
}

/** Browser popup notification title (plain — Ant Design `type` supplies the icon). */
export function formatTargetPopupTitle(status, targetName, isRecovery = false) {
  return formatTargetAlertTitle(status, targetName, isRecovery);
}

export function formatInfluxPopupTitle(targetName, isAnomaly = true) {
  return formatInfluxAlertTitle(targetName, isAnomaly);
}

/** Collapse legacy multi-line Influx anomaly text into one threshold line. */
export function normalizeInfluxAnomalyMessage(text, anomalyDetails = []) {
  if (!text || typeof text !== 'string') return text || 'Anomaly detected';
  const trimmed = text.trim();
  if (/^(Maximum|Minimum) threshold .+ exceeded$/i.test(trimmed)) return trimmed;
  if (/^Threshold limits exceeded/i.test(trimmed)) return trimmed;

  const details = Array.isArray(anomalyDetails) ? anomalyDetails : [];
  const sample = details.find((d) => d.message || d.reason)?.message
    || details.find((d) => d.message || d.reason)?.reason
    || trimmed;

  const maxMatch = sample.match(/maximum threshold ([\d.]+)/i);
  if (maxMatch) return `Maximum threshold ${maxMatch[1]} exceeded`;

  const minMatch = sample.match(/minimum threshold ([\d.]+)/i);
  if (minMatch) return `Minimum threshold ${minMatch[1]} exceeded`;

  if (/anomaly\(s\) detected/i.test(trimmed) && details.length === 0) {
    const inlineMax = trimmed.match(/maximum threshold ([\d.]+)/i);
    if (inlineMax) return `Maximum threshold ${inlineMax[1]} exceeded`;
    const inlineMin = trimmed.match(/minimum threshold ([\d.]+)/i);
    if (inlineMin) return `Minimum threshold ${inlineMin[1]} exceeded`;
  }

  return trimmed;
}

export function formatInfluxAnomalyDescription(data) {
  const text = data?.error || data?.summary || '';
  return normalizeInfluxAnomalyMessage(text, data?.anomalyDetails);
}

export function normalizeNotifyStatus(status) {
  return String(status || '').toUpperCase();
}

/**
 * Target toast / sound / Active Alerts.
 * - No UP/OK/recovery.
 * - No intermediate TIMEOUT (before consecutive-timeout threshold); UI still shows TIMEOUT.
 * - Notify on DOWN (includes promotion at threshold) and ERROR.
 */
export function shouldNotifyTargetStatus(data) {
  if (!data) return false;
  const status = normalizeNotifyStatus(data.status);
  if (status === 'UP' || status === 'OK') return false;
  if (data.isRecovery) return false;
  if (status === 'TIMEOUT') return false;
  return status === 'DOWN' || status === 'ERROR';
}

/** Influx toast — ANOMALY / DOWN / ERROR only. */
export function shouldNotifyInfluxStatus(status) {
  const s = normalizeNotifyStatus(status);
  return s === 'ANOMALY' || s === 'DOWN' || s === 'ERROR';
}

/** Rewrite legacy timeout→DOWN detail lines for display. */
export function normalizeTimeoutDownMessage(text) {
  if (!text || typeof text !== 'string') return text;
  const match = text.match(/\((\d+) consecutive timeouts,\s*threshold\s*\d+\s*[—-]\s*status DOWN\)/i);
  if (match) {
    const n = parseInt(match[1], 10);
    const word = n === 1 ? 'time' : 'times';
    return `Connection timed out ${n} consecutive ${word} — status changed to DOWN`;
  }
  return text;
}

/** Pick one error line for popups; backend often sends both message and errorMessage with overlap. */
export function resolveTargetErrorDetail(data) {
  const errorMessage = normalizeTimeoutDownMessage(String(data?.errorMessage || '').trim());
  let message = String(data?.message || '').trim();
  if (!errorMessage && !message) return null;
  if (!message) return errorMessage;
  if (!errorMessage) return message;
  if (message === errorMessage) return errorMessage;

  const status = String(data?.status || '').toUpperCase();
  if (status) {
    const prefixed = `${status} - ${errorMessage}`;
    if (message === prefixed) return errorMessage;
    const stripped = message.replace(/^(UP|DOWN|TIMEOUT|ERROR)\s*-\s*/i, '').trim();
    if (stripped === errorMessage) return errorMessage;
    if (errorMessage.includes(stripped) || stripped.includes(errorMessage)) {
      return errorMessage.length >= stripped.length ? errorMessage : stripped;
    }
  }

  if (message.includes(errorMessage)) return errorMessage;
  if (errorMessage.includes(message)) return errorMessage;

  return errorMessage;
}

export function getAlertStatusIcon(status, isRecovery = false, style = {}) {
  const iconStyle = { fontSize: 18, ...style };
  if (isRecovery) {
    return <CheckCircleOutlined style={{ ...iconStyle, color: '#52c41a' }} />;
  }
  switch (String(status || '').toUpperCase()) {
    case 'UP':
      return <CheckCircleOutlined style={{ ...iconStyle, color: '#52c41a' }} />;
    case 'DOWN':
      return <CloseCircleOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />;
    case 'TIMEOUT':
      return <ClockCircleOutlined style={{ ...iconStyle, color: '#faad14' }} />;
    case 'ANOMALY':
      return <ExclamationCircleOutlined style={{ ...iconStyle, color: '#faad14' }} />;
    case 'ERROR':
      return <CloseCircleOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />;
    default:
      return <WarningOutlined style={{ ...iconStyle, color: '#faad14' }} />;
  }
}

export function resolveAlertPresentation(alert) {
  const data = alert?.data || {};
  let status = data.status;
  if (!status && alert?.type === 'influxdb') status = 'ANOMALY';
  if (!status && /Recovered:/i.test(alert?.title)) status = 'UP';
  if (!status && /DOWN:/i.test(alert?.title)) status = 'DOWN';
  if (!status && alert?.severity === 'CRITICAL') status = 'DOWN';
  if (!status) status = 'ANOMALY';

  const isRecovery = Boolean(data.isRecovery || /Recovered:/i.test(alert?.title));
  const targetName = data.targetName || data.target_name;
  let title = stripAlertTitlePrefix(alert?.title);
  if (targetName) {
    title = isRecovery ? `Recovered: ${targetName}` : `${status}: ${targetName}`;
  }

  return { status, isRecovery, title };
}

export function bulletLine(text) {
  return `- ${text}`;
}

export function monitoredItemTypeLabel(itemType) {
  switch (itemType) {
    case 'process':
      return 'Process';
    case 'service':
    case 'windows_service':
    case 'linux_service':
      return 'Service';
    case 'pm2':
      return 'PM2';
    default:
      return 'Item';
  }
}

export function severityIssuePrefix(severity) {
  return severity === 'critical' ? 'Critical' : 'Warning';
}

export function formatTargetLatency(latency) {
  if (latency == null || latency === '') return '-';
  const n = Number(latency);
  return Number.isFinite(n) ? `${n} ms` : String(latency);
}

export function buildTargetStatusPopupFields(data, target = {}, extra = {}) {
  const host = data.host || target.host || '-';
  const port = data.port ?? target.port;
  const hostDisplay = port != null && port !== '' ? `${host}:${port}` : host;
  const errorDetail = resolveTargetErrorDetail(data);

  let description = '-';
  if (extra.isRecovery) {
    description = 'Connection restored';
  } else if (errorDetail) {
    description = errorDetail;
  }

  return {
    host: hostDisplay,
    description,
    emailStatus: extra.isRecovery ? null : buildTargetEmailStatusLabel(data),
  };
}

/** Email line for monitor alerts — shown below Keterangan. */
export function buildTargetEmailStatusLabel(data) {
  if (!data) return null;
  if (data.emailSent === true || data.emailThresholdReached === true) {
    return 'sent';
  }
  if (data.emailThreshold != null || data.emailConsecutiveFailures != null) {
    return data.emailSent === false && !data.emailThresholdReached ? 'buffered' : 'sent';
  }
  const status = String(data.status || '').toUpperCase();
  if (status === 'DOWN' || status === 'ERROR') {
    return 'sent';
  }
  return null;
}

export function buildTargetEmailFailureNote(data) {
  const status = buildTargetEmailStatusLabel(data);
  return status ? `Email: ${status}` : null;
}

/** Plain-text body for Active Alerts panel (title already shows status + target name). */
export function buildTargetStatusPopupDescription(data, target = {}, extra = {}) {
  const { host, description, emailStatus } = buildTargetStatusPopupFields(data, target, extra);
  const lines = [
    `Host: ${host}`,
    `Keterangan: ${description}`,
  ];
  if (emailStatus) {
    lines.push(`Email: ${emailStatus}`);
  }
  return lines.join('\n');
}

/** Structured popup body — preserves line breaks in Ant Design notification. */
export function TargetStatusPopupDescription({ data, target = {}, isRecovery = false }) {
  const fields = buildTargetStatusPopupFields(data, target, { isRecovery });

  return (
    <div className="target-alert-popup-desc">
      <div className="target-alert-popup-field">
        <span className="target-alert-popup-label">Host:</span>
        {' '}
        {fields.host}
      </div>
      <div className="target-alert-popup-field">
        <span className="target-alert-popup-label">Keterangan:</span>
        {' '}
        {fields.description}
      </div>
      {fields.emailStatus ? (
        <div className="target-alert-popup-field">
          <span className="target-alert-popup-label">Email:</span>
          {' '}
          {fields.emailStatus}
        </div>
      ) : null}
    </div>
  );
}
